import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { useTenant } from '@/hooks/useTenant';
import { useLanguage } from '@/hooks/useLanguage';

export function Header() {
 const [isMenuOpen, setIsMenuOpen] = useState(false);
 const { config } = useTenant();
 const { t, isMultiLanguageEnabled } = useLanguage();

 if (!config) return null;

 const navigation = [
 { name: t('nav.home'), href: '/' },
 { name: t('nav.sales'), href: '/ventas' },
 { name: t('nav.rentals'), href: '/alquiler' },
 { name: t('nav.services'), href: '/servicios' },
 { name: t('nav.about'), href: '/quienes-somos' },
 ...(config.features.blog ? [{ name: t('nav.blog'), href: '/blog' }] : []),
 { name: t('nav.contact'), href: '/contacto' },
 { name: t('nav.sellWithUs', 'Vende con nosotros'), href: '/vender', highlight: true },
 ];

 return (
 <header className="bg-background shadow-sm border-b border-border sticky top-0 z-50">
 {/* Top Bar */}
 <div className="hidden md:flex bg-muted py-2">
 <div className="container mx-auto px-4 flex justify-between items-center text-sm text-muted-foreground">
 <div className="flex items-center space-x-4">
 <div className="flex items-center space-x-1">
 <Phone className="h-4 w-4" />
 <span>{config.contact.phone}</span>
 </div>
 <div className="flex items-center space-x-1">
 <Mail className="h-4 w-4" />
 <span>{config.contact.email}</span>
 </div>
 </div>
 <div className="flex items-center space-x-3">
 {/* Solo mostrar selector de idiomas si está habilitado para el tenant */}
 {isMultiLanguageEnabled && (
 <LanguageSwitcher 
 variant="compact" 
 showFlags={true}
 className="hidden sm:flex"
 />
 )}
 </div>
 </div>
 </div>

 {/* Main Header */}
 <div className="container mx-auto px-4">
 <div className="flex items-center justify-between h-16">
 {/* Logo */}
 <Link to="/" className="flex items-center space-x-2">
 <div className="flex items-center">
 {config.logo ? (
 <img src={config.logo} alt={config.name} className="h-8 w-auto" />
 ) : (
 <div className="w-8 h-8 rounded-sm flex items-center justify-center bg-primary text-primary-foreground font-semibold text-base">
 {config.name.charAt(0)}
 </div>
 )}
 <span className="ml-2 text-lg font-semibold text-foreground tracking-tight">
 {config.name}
 </span>
 </div>
 </Link>

 {/* Desktop Navigation */}
 <nav className="hidden md:flex items-center space-x-6">
 {navigation.map((item) => (
 <Link
 key={item.name}
 to={item.href}
 className={
 (item as any).highlight
 ? 'inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors'
 : 'text-foreground/80 hover:text-primary px-3 py-2 text-sm font-medium transition-colors'
 }
 >
 {item.name}
 </Link>
 ))}
 </nav>

 {/* Mobile menu button */}
 <div className="md:hidden flex items-center space-x-2">
 {isMultiLanguageEnabled && (
 <LanguageSwitcher 
 variant="compact" 
 showFlags={true}
 className="sm:hidden"
 />
 )}
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setIsMenuOpen(!isMenuOpen)}
 >
 {isMenuOpen ? (
 <X className="h-6 w-6" />
 ) : (
 <Menu className="h-6 w-6" />
 )}
 </Button>
 </div>
 </div>

 {/* Mobile Navigation */}
 {isMenuOpen && (
 <div className="md:hidden py-4 border-t border-border">
 <nav className="flex flex-col space-y-2">
 {navigation.map((item) => (
 <Link
 key={item.name}
 to={item.href}
 className="text-foreground/80 hover:text-primary px-3 py-2 text-base font-medium transition-colors"
 onClick={() => setIsMenuOpen(false)}
 >
 {item.name}
 </Link>
 ))}

 {/* Mobile Language Switcher */}
 {isMultiLanguageEnabled && (
 <div className="px-3 py-2 border-t border-border mt-4 pt-4">
 <div className="text-sm text-muted-foreground mb-2">Idioma:</div>
 <LanguageSwitcher 
 variant="buttons" 
 showFlags={true}
 />
 </div>
 )}
 </nav>
 </div>
 )}
 </div>
 </header>
 );
}